import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './Portal.css';

function MLSAgentPortal({ user }) {
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedListing, setSelectedListing] = useState(null);
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [showOfferModal, setShowOfferModal] = useState(false);
  const [mlsNumber, setMlsNumber] = useState('');
  const [notes, setNotes] = useState('');
  const [activeTab, setActiveTab] = useState('assignments');

  // External offer form state
  const [offerData, setOfferData] = useState({
    buyerName: '',
    buyerEmail: '',
    buyerPhone: '',
    buyerAgentName: '',
    buyerAgentEmail: '',
    buyerAgentPhone: '',
    buyerAgentLicense: '',
    buyerAgentBrokerage: '',
    offerAmount: '',
    earnestMoney: '',
    optionFee: '',
    optionDays: '10',
    financingType: 'conventional',
    downPaymentPercent: '20',
    closingDate: '',
    contingencies: '',
    specialProvisions: '',
    offerPdf: null
  });
  const [uploadingOffer, setUploadingOffer] = useState(false);

  useEffect(() => {
    fetchAssignments();
  }, []);

  const fetchAssignments = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/mls-agent/assignments', {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = Array.isArray(response.data) ? response.data : (response.data.assignments || []);
      setAssignments(data);
    } catch (error) {
      console.error('Error fetching assignments:', error);
      setAssignments([]);
    } finally {
      setLoading(false);
    }
  };

  const updateMLSStatus = async (assignmentId, newStatus) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`/api/mls-agent/assignment/${assignmentId}/status`, 
        { status: newStatus, mls_number: mlsNumber, notes },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      alert('Status updated!');
      setShowUpdateModal(false);
      setMlsNumber('');
      setNotes('');
      fetchAssignments();
    } catch (error) {
      console.error('Error updating status:', error);
      alert('Error updating status');
      setShowUpdateModal(false);
    }
  };

  const markAsListed = async (assignment) => {
    if (!mlsNumber) {
      alert('Please enter the MLS number');
      return;
    }
    await updateMLSStatus(assignment.id, 'mls_listed');
  };

  const handleOfferChange = (e) => {
    const { name, value, files } = e.target;
    if (files) {
      setOfferData({ ...offerData, [name]: files[0] });
    } else {
      setOfferData({ ...offerData, [name]: value });
    }
  };

  const handleSubmitExternalOffer = async (e) => {
    e.preventDefault();
    
    if (!selectedListing) {
      alert('Please select a listing first');
      return;
    }

    if (!offerData.offerAmount || !offerData.buyerName) {
      alert('Please enter buyer name and offer amount');
      return;
    }

    setUploadingOffer(true);

    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      
      formData.append('listing_id', selectedListing.listing_id);
      formData.append('buyer_name', offerData.buyerName);
      formData.append('buyer_email', offerData.buyerEmail);
      formData.append('buyer_phone', offerData.buyerPhone);
      formData.append('buyer_agent_name', offerData.buyerAgentName);
      formData.append('buyer_agent_email', offerData.buyerAgentEmail);
      formData.append('buyer_agent_phone', offerData.buyerAgentPhone);
      formData.append('buyer_agent_license', offerData.buyerAgentLicense);
      formData.append('buyer_agent_brokerage', offerData.buyerAgentBrokerage);
      formData.append('offer_amount', offerData.offerAmount);
      formData.append('earnest_money', offerData.earnestMoney);
      formData.append('option_fee', offerData.optionFee);
      formData.append('option_days', offerData.optionDays);
      formData.append('financing_type', offerData.financingType);
      formData.append('down_payment_percent', offerData.downPaymentPercent);
      formData.append('closing_date', offerData.closingDate);
      formData.append('contingencies', offerData.contingencies);
      formData.append('special_provisions', offerData.specialProvisions);
      
      if (offerData.offerPdf) {
        formData.append('offer_pdf', offerData.offerPdf);
      }

      await axios.post('/api/mls-agent/upload-external-offer', formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      alert('Offer uploaded successfully! The seller will be notified.');
      setShowOfferModal(false);
      resetOfferForm();
    } catch (error) {
      console.error('Error uploading offer:', error);
      alert(error.response?.data?.error || 'Failed to upload offer');
    } finally {
      setUploadingOffer(false);
    }
  };

  const resetOfferForm = () => {
    setOfferData({
      buyerName: '',
      buyerEmail: '',
      buyerPhone: '',
      buyerAgentName: '',
      buyerAgentEmail: '',
      buyerAgentPhone: '',
      buyerAgentLicense: '',
      buyerAgentBrokerage: '',
      offerAmount: '',
      earnestMoney: '',
      optionFee: '',
      optionDays: '10',
      financingType: 'conventional',
      downPaymentPercent: '20',
      closingDate: '',
      contingencies: '',
      specialProvisions: '',
      offerPdf: null
    });
    setSelectedListing(null);
  };

  const getStatusBadge = (status) => {
    const badges = {
      'agent_assigned': { class: 'badge-info', text: 'Newly Assigned' },
      'listing_prepared': { class: 'badge-warning', text: 'Preparing Listing' },
      'photos_scheduled': { class: 'badge-warning', text: 'Photos Scheduled' },
      'mls_pending': { class: 'badge-warning', text: 'Pending MLS Entry' },
      'mls_listed': { class: 'badge-success', text: 'Live on MLS' },
      'under_contract': { class: 'badge-info', text: 'Under Contract' },
      'sold': { class: 'badge-success', text: 'Sold' }
    };
    return badges[status] || { class: 'badge-secondary', text: status || 'Unknown' };
  };

  const activeListings = assignments.filter(a => a.status === 'mls_listed');

  if (!user) {
    return <div className="portal"><p>Loading...</p></div>;
  }

  return (
    <div className="portal">
      <div className="portal-header">
        <h1>MLS Agent Portal</h1>
        <p>Welcome, {user.firstName}! Manage your flat-fee MLS listing assignments.</p>
      </div>

      <div className="stats-dashboard">
        <div className="stat-card">
          <div className="stat-number">{assignments.length}</div>
          <div className="stat-label">Total Assignments</div>
        </div>
        <div className="stat-card">
          <div className="stat-number">
            {assignments.filter(a => a.status === 'agent_assigned').length}
          </div>
          <div className="stat-label">New Assignments</div>
        </div>
        <div className="stat-card">
          <div className="stat-number">
            {assignments.filter(a => a.status === 'mls_listed').length}
          </div>
          <div className="stat-label">Active Listings</div>
        </div>
        <div className="stat-card">
          <div className="stat-number">
            {assignments.filter(a => a.status === 'sold').length}
          </div>
          <div className="stat-label">Sold</div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="portal-tabs">
        <button 
          className={`tab-btn ${activeTab === 'assignments' ? 'active' : ''}`}
          onClick={() => setActiveTab('assignments')}
        >
          My Assignments
        </button>
        <button 
          className={`tab-btn ${activeTab === 'upload-offer' ? 'active' : ''}`}
          onClick={() => setActiveTab('upload-offer')}
        >
          Upload External Offer
        </button>
      </div>

      <div className="portal-content">
        {/* Assignments Tab */}
        {activeTab === 'assignments' && (
          <>
            <div className="section-header">
              <h2>My Assignments</h2>
            </div>

            {loading ? (
              <p>Loading...</p>
            ) : assignments.length === 0 ? (
              <div className="empty-state">
                <p>No assignments yet. New listings will appear here when assigned to you.</p>
              </div>
            ) : (
              <div className="assignments-list">
                {assignments.map(assignment => {
                  const badge = getStatusBadge(assignment.status);
                  return (
                    <div key={assignment.id} className="assignment-card">
                      <div className="assignment-header">
                        <div>
                          <h3>{assignment.listing_title}</h3>
                          <p className="assignment-address">{assignment.listing_address}</p>
                        </div>
                        <div className="assignment-meta">
                          <span className={`badge ${badge.class}`}>
                            {badge.text}
                          </span>
                          <span className="assignment-price">
                            ${assignment.listing_price?.toLocaleString()}
                          </span>
                        </div>
                      </div>

                      <div className="seller-contact">
                        <h4>Seller Contact</h4>
                        <div className="contact-grid">
                          <div><strong>Name:</strong> {assignment.seller_name}</div>
                          <div><strong>Email:</strong> {assignment.seller_email}</div>
                          <div><strong>Phone:</strong> {assignment.seller_phone || 'Not provided'}</div>
                        </div>
                      </div>

                      <div className="listing-info">
                        <h4>Listing Information</h4>
                        <div className="info-section">
                          <strong>Description:</strong>
                          <p>{assignment.property_description || 'No description provided'}</p>
                        </div>
                        <div className="info-section">
                          <strong>Special Features:</strong>
                          <p>{assignment.special_features || 'None listed'}</p>
                        </div>
                        <div className="info-section">
                          <strong>Showing Instructions:</strong>
                          <p>{assignment.showing_instructions || 'Contact agent'}</p>
                        </div>
                        {assignment.lockbox_code && (
                          <div className="info-section lockbox">
                            <strong>Lockbox Code:</strong>
                            <span className="lockbox-code">{assignment.lockbox_code}</span>
                          </div>
                        )}
                      </div>

                      {assignment.mls_number && (
                        <div className="mls-info">
                          <strong>MLS #:</strong> {assignment.mls_number}
                        </div>
                      )}

                      <div className="assignment-actions">
                        {assignment.status === 'agent_assigned' && (
                          <button 
                            className="btn btn-primary"
                            onClick={() => updateMLSStatus(assignment.id, 'listing_prepared')}
                          >
                            Start Working on Listing
                          </button>
                        )}
                        
                        {assignment.status === 'listing_prepared' && (
                          <button 
                            className="btn btn-primary"
                            onClick={() => updateMLSStatus(assignment.id, 'photos_scheduled')}
                          >
                            Photos Scheduled
                          </button>
                        )}

                        {assignment.status === 'photos_scheduled' && (
                          <button 
                            className="btn btn-primary"
                            onClick={() => updateMLSStatus(assignment.id, 'mls_pending')}
                          >
                            Ready for MLS Entry
                          </button>
                        )}

                        {assignment.status === 'mls_pending' && (
                          <button 
                            className="btn btn-success"
                            onClick={() => {
                              setSelectedListing(assignment);
                              setShowUpdateModal(true);
                            }}
                          >
                            Mark as Listed on MLS
                          </button>
                        )}

                        {assignment.status === 'mls_listed' && (
                          <>
                            <button 
                              className="btn btn-primary"
                              onClick={() => {
                                setSelectedListing(assignment);
                                setShowOfferModal(true);
                              }}
                            >
                              Upload Offer
                            </button>
                            <button 
                              className="btn btn-info"
                              onClick={() => updateMLSStatus(assignment.id, 'under_contract')}
                            >
                              Mark Under Contract
                            </button>
                          </>
                        )}

                        {assignment.status === 'under_contract' && (
                          <button 
                            className="btn btn-success"
                            onClick={() => updateMLSStatus(assignment.id, 'sold')}
                          >
                            Mark as Sold
                          </button>
                        )}

                        <button className="btn btn-secondary">
                          Contact Seller
                        </button>
                      </div>

                      <div className="assignment-footer">
                        <span>Assigned: {new Date(assignment.assigned_at).toLocaleDateString()}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}

        {/* Upload External Offer Tab */}
        {activeTab === 'upload-offer' && (
          <>
            <div className="section-header">
              <h2>Upload External Offer</h2>
              <p>Upload offers received from buyer's agents via MLS</p>
            </div>

            {activeListings.length === 0 ? (
              <div className="empty-state">
                <p>No active MLS listings. Offers can only be uploaded for listings that are live on MLS.</p>
              </div>
            ) : (
              <div className="external-offer-form">
                <div className="form-group">
                  <label>Select Listing *</label>
                  <select 
                    value={selectedListing?.id || ''} 
                    onChange={(e) => {
                      const listing = activeListings.find(l => l.id === parseInt(e.target.value));
                      setSelectedListing(listing);
                    }}
                    required
                  >
                    <option value="">-- Select a listing --</option>
                    {activeListings.map(listing => (
                      <option key={listing.id} value={listing.id}>
                        {listing.listing_address} - ${listing.listing_price?.toLocaleString()}
                      </option>
                    ))}
                  </select>
                </div>

                {selectedListing && (
                  <form onSubmit={handleSubmitExternalOffer}>
                    <div className="form-section">
                      <h3>Buyer Information</h3>
                      <div className="form-row">
                        <div className="form-group">
                          <label>Buyer Name *</label>
                          <input
                            type="text"
                            name="buyerName"
                            value={offerData.buyerName}
                            onChange={handleOfferChange}
                            required
                          />
                        </div>
                        <div className="form-group">
                          <label>Buyer Email</label>
                          <input
                            type="email"
                            name="buyerEmail"
                            value={offerData.buyerEmail}
                            onChange={handleOfferChange}
                          />
                        </div>
                        <div className="form-group">
                          <label>Buyer Phone</label>
                          <input
                            type="tel"
                            name="buyerPhone"
                            value={offerData.buyerPhone}
                            onChange={handleOfferChange}
                          />
                        </div>
                      </div>
                    </div>

                    <div className="form-section">
                      <h3>Buyer's Agent Information</h3>
                      <div className="form-row">
                        <div className="form-group">
                          <label>Agent Name *</label>
                          <input
                            type="text"
                            name="buyerAgentName"
                            value={offerData.buyerAgentName}
                            onChange={handleOfferChange}
                            required
                          />
                        </div>
                        <div className="form-group">
                          <label>Agent Email</label>
                          <input
                            type="email"
                            name="buyerAgentEmail"
                            value={offerData.buyerAgentEmail}
                            onChange={handleOfferChange}
                          />
                        </div>
                        <div className="form-group">
                          <label>Agent Phone</label>
                          <input
                            type="tel"
                            name="buyerAgentPhone"
                            value={offerData.buyerAgentPhone}
                            onChange={handleOfferChange}
                          />
                        </div>
                      </div>
                      <div className="form-row">
                        <div className="form-group">
                          <label>License #</label>
                          <input
                            type="text"
                            name="buyerAgentLicense"
                            value={offerData.buyerAgentLicense}
                            onChange={handleOfferChange}
                          />
                        </div>
                        <div className="form-group">
                          <label>Brokerage</label>
                          <input
                            type="text"
                            name="buyerAgentBrokerage"
                            value={offerData.buyerAgentBrokerage}
                            onChange={handleOfferChange}
                          />
                        </div>
                      </div>
                    </div>

                    <div className="form-section">
                      <h3>Offer Terms</h3>
                      <div className="form-row">
                        <div className="form-group">
                          <label>Offer Amount *</label>
                          <input
                            type="number"
                            name="offerAmount"
                            value={offerData.offerAmount}
                            onChange={handleOfferChange}
                            required
                          />
                        </div>
                        <div className="form-group">
                          <label>Earnest Money</label>
                          <input
                            type="number"
                            name="earnestMoney"
                            value={offerData.earnestMoney}
                            onChange={handleOfferChange}
                          />
                        </div>
                        <div className="form-group">
                          <label>Option Fee</label>
                          <input
                            type="number"
                            name="optionFee"
                            value={offerData.optionFee}
                            onChange={handleOfferChange}
                          />
                        </div>
                      </div>
                      <div className="form-row">
                        <div className="form-group">
                          <label>Option Days</label>
                          <input
                            type="number"
                            name="optionDays"
                            value={offerData.optionDays}
                            onChange={handleOfferChange}
                          />
                        </div>
                        <div className="form-group">
                          <label>Financing Type</label>
                          <select
                            name="financingType"
                            value={offerData.financingType}
                            onChange={handleOfferChange}
                          >
                            <option value="cash">Cash</option>
                            <option value="conventional">Conventional</option>
                            <option value="fha">FHA</option>
                            <option value="va">VA</option>
                            <option value="usda">USDA</option>
                            <option value="seller_finance">Seller Finance</option>
                          </select>
                        </div>
                        <div className="form-group">
                          <label>Down Payment %</label>
                          <input
                            type="number"
                            name="downPaymentPercent"
                            value={offerData.downPaymentPercent}
                            onChange={handleOfferChange}
                          />
                        </div>
                      </div>
                      <div className="form-row">
                        <div className="form-group">
                          <label>Closing Date</label>
                          <input
                            type="date"
                            name="closingDate"
                            value={offerData.closingDate}
                            onChange={handleOfferChange}
                          />
                        </div>
                      </div>
                    </div>

                    <div className="form-section">
                      <h3>Additional Details</h3>
                      <div className="form-group">
                        <label>Contingencies</label>
                        <textarea
                          name="contingencies"
                          value={offerData.contingencies}
                          onChange={handleOfferChange}
                          rows={3}
                          placeholder="Inspection, appraisal, financing, etc."
                        />
                      </div>
                      <div className="form-group">
                        <label>Special Provisions</label>
                        <textarea
                          name="specialProvisions"
                          value={offerData.specialProvisions}
                          onChange={handleOfferChange}
                          rows={3}
                          placeholder="Any special terms or conditions"
                        />
                      </div>
                    </div>

                    <div className="form-section">
                      <h3>Offer Document</h3>
                      <div className="form-group">
                        <label>Upload Signed Offer (PDF)</label>
                        <input
                          type="file"
                          name="offerPdf"
                          onChange={handleOfferChange}
                          accept=".pdf"
                        />
                        <span className="form-hint">Upload the signed TREC contract from the buyer's agent</span>
                      </div>
                    </div>

                    <div className="form-actions">
                      <button 
                        type="submit" 
                        className="btn btn-primary btn-block"
                        disabled={uploadingOffer}
                      >
                        {uploadingOffer ? 'Uploading...' : 'Submit Offer to Seller'}
                      </button>
                    </div>
                  </form>
                )}
              </div>
            )}
          </>
        )}
      </div>

      {/* MLS Number Modal */}
      {showUpdateModal && selectedListing && (
        <div className="modal-overlay" onClick={() => setShowUpdateModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Enter MLS Details</h3>
              <button className="modal-close" onClick={() => setShowUpdateModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <p><strong>Listing:</strong> {selectedListing.listing_title}</p>
              
              <div className="form-group">
                <label>MLS Number *</label>
                <input
                  type="text"
                  value={mlsNumber}
                  onChange={(e) => setMlsNumber(e.target.value)}
                  placeholder="Enter MLS #"
                  required
                />
              </div>

              <div className="form-group">
                <label>Notes (optional)</label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Any notes about the listing..."
                  rows={3}
                />
              </div>

              <button 
                className="btn btn-success btn-block"
                onClick={() => markAsListed(selectedListing)}
              >
                Confirm Listed on MLS
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Quick Offer Upload Modal (from assignment card) */}
      {showOfferModal && selectedListing && (
        <div className="modal-overlay" onClick={() => setShowOfferModal(false)}>
          <div className="modal-content modal-large" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Upload External Offer</h3>
              <button className="modal-close" onClick={() => { setShowOfferModal(false); resetOfferForm(); }}>×</button>
            </div>
            <div className="modal-body">
              <p><strong>Listing:</strong> {selectedListing.listing_address}</p>
              <p><strong>Price:</strong> ${selectedListing.listing_price?.toLocaleString()}</p>
              
              <form onSubmit={handleSubmitExternalOffer}>
                <div className="form-row">
                  <div className="form-group">
                    <label>Buyer Name *</label>
                    <input
                      type="text"
                      name="buyerName"
                      value={offerData.buyerName}
                      onChange={handleOfferChange}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Buyer Agent Name *</label>
                    <input
                      type="text"
                      name="buyerAgentName"
                      value={offerData.buyerAgentName}
                      onChange={handleOfferChange}
                      required
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Buyer Agent Email</label>
                    <input
                      type="email"
                      name="buyerAgentEmail"
                      value={offerData.buyerAgentEmail}
                      onChange={handleOfferChange}
                    />
                  </div>
                  <div className="form-group">
                    <label>Buyer Agent Phone</label>
                    <input
                      type="tel"
                      name="buyerAgentPhone"
                      value={offerData.buyerAgentPhone}
                      onChange={handleOfferChange}
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Offer Amount *</label>
                    <input
                      type="number"
                      name="offerAmount"
                      value={offerData.offerAmount}
                      onChange={handleOfferChange}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Financing Type</label>
                    <select
                      name="financingType"
                      value={offerData.financingType}
                      onChange={handleOfferChange}
                    >
                      <option value="cash">Cash</option>
                      <option value="conventional">Conventional</option>
                      <option value="fha">FHA</option>
                      <option value="va">VA</option>
                      <option value="usda">USDA</option>
                      <option value="seller_finance">Seller Finance</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Closing Date</label>
                    <input
                      type="date"
                      name="closingDate"
                      value={offerData.closingDate}
                      onChange={handleOfferChange}
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label>Upload Signed Offer (PDF)</label>
                  <input
                    type="file"
                    name="offerPdf"
                    onChange={handleOfferChange}
                    accept=".pdf"
                  />
                </div>

                <button 
                  type="submit" 
                  className="btn btn-primary btn-block"
                  disabled={uploadingOffer}
                >
                  {uploadingOffer ? 'Uploading...' : 'Submit Offer'}
                </button>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default MLSAgentPortal;
